// Top-of-canvas navigation strip for the flow grammar.
//
// Each statement renders as a clickable chip; clicking pans/zooms the
// canvas to that node via the shared focus controller. Filter tabs at
// the left scope the strip to a single statement type (or to #ui events
// only). The currently-active card — derived in FlowCanvas from the
// viewport center on pan/zoom-end — is highlighted and auto-scrolled
// into view inside the strip.
//
// A second-row "Bounded context" selector scopes both the canvas and
// the chip strip to one BC + the BCs it shares chains with. Useful for
// customer walkthroughs of large imported flows where the unfiltered
// canvas reads as one giant horizontal strip.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ParsedFlow, Statement, StatementType } from '../utils/flowModel';
import {
  getBoundedContexts,
  computeRelatedBcs,
  computeFilteredStatementIds,
  type BoundedContextInfo,
} from '../utils/flowBoundedContext';

type FilterKind = 'all' | StatementType | 'ui';

const FILTERS: { key: FilterKind; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'action', label: 'Actions' },
  { key: 'query', label: 'Queries' },
  { key: 'command', label: 'Commands' },
  { key: 'event', label: 'Events' },
  { key: 'reaction', label: 'Reactions' },
  { key: 'ui', label: '#ui' },
];

interface Props {
  flow: ParsedFlow;
  activeStmtId: number | null;
  onJump: (statementId: number) => void;
  selectedBoundedContext: string | null;
  onBoundedContextChange: (bc: string | null) => void;
}

const FlowNavBar: React.FC<Props> = ({
  flow,
  activeStmtId,
  onJump,
  selectedBoundedContext,
  onBoundedContextChange,
}) => {
  const [filter, setFilter] = useState<FilterKind>('all');
  const [bcMenuOpen, setBcMenuOpen] = useState(false);

  const allBcs = useMemo(() => getBoundedContexts(flow), [flow]);
  const relatedBcs = useMemo(
    () => (selectedBoundedContext ? computeRelatedBcs(flow, selectedBoundedContext) : new Set<string>()),
    [flow, selectedBoundedContext],
  );
  // Use the same filter the layout uses so the chip strip and the
  // canvas always agree on what's visible. Without this, the strip
  // could show humans/externals that aren't in any chain touching the
  // primary BC (and thus aren't on the canvas).
  const filteredStatements = useMemo(() => {
    if (!selectedBoundedContext) return flow.statements;
    const visibleIds = computeFilteredStatementIds(flow, selectedBoundedContext);
    return flow.statements.filter((s) => visibleIds.has(s.id));
  }, [flow, selectedBoundedContext]);

  const counts = useMemo(() => {
    const out: Record<FilterKind, number> = {
      all: 0, action: 0, query: 0, command: 0, event: 0, reaction: 0, ui: 0,
    };
    for (const s of filteredStatements) {
      out.all += 1;
      out[s.type] += 1;
      if (s.type === 'event' && s.tags?.ui === true) out.ui += 1;
    }
    return out;
  }, [filteredStatements]);

  const visible = useMemo(() => {
    const sorted = [...filteredStatements].sort((a, b) => a.id - b.id);
    if (filter === 'all') return sorted;
    if (filter === 'ui') return sorted.filter((s) => s.type === 'event' && s.tags?.ui === true);
    return sorted.filter((s) => s.type === filter);
  }, [filteredStatements, filter]);

  // Auto-scroll the active chip into view when activeStmtId changes.
  const chipRefs = useRef<Map<number, HTMLButtonElement | null>>(new Map());
  useEffect(() => {
    if (activeStmtId == null) return;
    const el = chipRefs.current.get(activeStmtId);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [activeStmtId]);

  // Close the BC menu on outside click / Esc.
  const bcWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!bcMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (bcWrapRef.current && !bcWrapRef.current.contains(e.target as Node)) setBcMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBcMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [bcMenuOpen]);

  const hasBcs = allBcs.length > 0;
  const selectedInfo = selectedBoundedContext
    ? allBcs.find((b) => b.name === selectedBoundedContext) ?? null
    : null;

  return (
    <div className="flow-nav-bar">
      <div className="flow-nav-row">
        <div className="flow-nav-filters" role="tablist">
          {FILTERS.map((f) => {
            const n = counts[f.key];
            const disabled = n === 0 && f.key !== 'all';
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                disabled={disabled}
                className={`fnb-filter ${filter === f.key ? 'fnb-filter--active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                <span className="fnb-filter-count">{n}</span>
              </button>
            );
          })}
        </div>
        {hasBcs && (
          <div className="fnb-bc-wrap" ref={bcWrapRef}>
            <button
              type="button"
              className={`fnb-bc-trigger ${selectedBoundedContext ? 'fnb-bc-trigger--active' : ''}`}
              onClick={() => setBcMenuOpen((o) => !o)}
              title="Filter the canvas to one bounded context (and the BCs it shares chains with)"
            >
              <span className="fnb-bc-label">Bounded context</span>
              <span className="fnb-bc-current">
                {selectedInfo ? selectedInfo.name : `All (${allBcs.length})`}
              </span>
              <span className="fnb-bc-chev">▾</span>
            </button>
            {bcMenuOpen && (
              <BcMenu
                allBcs={allBcs}
                selected={selectedBoundedContext}
                onSelect={(name) => {
                  onBoundedContextChange(name);
                  setBcMenuOpen(false);
                }}
              />
            )}
          </div>
        )}
      </div>
      {selectedBoundedContext && relatedBcs.size > 0 && (
        <div className="fnb-related" role="toolbar" aria-label="Related bounded contexts">
          <span className="fnb-related-label">Related ({relatedBcs.size}):</span>
          {[...relatedBcs]
            .map((name) => allBcs.find((b) => b.name === name) ?? { name, statementCount: 0 })
            .sort((a, b) => b.statementCount - a.statementCount)
            .map((bc) => (
              <button
                key={bc.name}
                type="button"
                className="fnb-related-chip"
                onClick={() => onBoundedContextChange(bc.name)}
                title={`Pivot the filter to ${bc.name}`}
              >
                {bc.name}
                <span className="fnb-related-count">{bc.statementCount}</span>
              </button>
            ))}
        </div>
      )}
      <div className="flow-nav-strip" role="toolbar" aria-label="Flow navigation">
        {visible.length === 0 ? (
          <span className="fnb-empty">No {filter === 'all' ? 'statements' : `${filter} statements`} yet</span>
        ) : (
          visible.map((s) => (
            <ChipButton
              key={s.id}
              statement={s}
              active={s.id === activeStmtId}
              onClick={() => onJump(s.id)}
              registerRef={(el) => chipRefs.current.set(s.id, el)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default FlowNavBar;

function BcMenu({
  allBcs,
  selected,
  onSelect,
}: {
  allBcs: BoundedContextInfo[];
  selected: string | null;
  onSelect: (name: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allBcs;
    return allBcs.filter((b) => b.name.toLowerCase().includes(q));
  }, [allBcs, query]);

  return (
    <div className="fnb-bc-menu" role="listbox">
      <input
        ref={inputRef}
        type="text"
        className="fnb-bc-search"
        placeholder="Search bounded contexts…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <button
        type="button"
        className={`fnb-bc-item ${selected === null ? 'fnb-bc-item--active' : ''}`}
        onClick={() => onSelect(null)}
      >
        <span className="fnb-bc-item-name">All bounded contexts</span>
        <span className="fnb-bc-item-count">{allBcs.length}</span>
      </button>
      <div className="fnb-bc-divider" />
      <div className="fnb-bc-list">
        {filtered.length === 0 && (
          <div className="fnb-bc-empty">No matches</div>
        )}
        {filtered.map((b) => (
          <button
            key={b.name}
            type="button"
            className={`fnb-bc-item ${selected === b.name ? 'fnb-bc-item--active' : ''}`}
            onClick={() => onSelect(b.name)}
            title={`${b.statementCount} statement${b.statementCount === 1 ? '' : 's'}`}
          >
            <span className="fnb-bc-item-name">{b.name}</span>
            <span className="fnb-bc-item-count">{b.statementCount}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipButton({
  statement,
  active,
  onClick,
  registerRef,
}: {
  statement: Statement;
  active: boolean;
  onClick: () => void;
  registerRef: (el: HTMLButtonElement | null) => void;
}) {
  const isUi = statement.type === 'event' && statement.tags?.ui === true;
  return (
    <button
      ref={registerRef}
      type="button"
      className={`fnb-chip fnb-chip--${statement.type}${active ? ' fnb-chip--active' : ''}${isUi ? ' fnb-chip--ui' : ''}`}
      onClick={onClick}
      title={`[${statement.id}] ${statement.name || '(unnamed)'} — ${statement.type}`}
    >
      <span className="fnb-chip-id">[{statement.id}]</span>
      <span className="fnb-chip-name">{statement.name || '(unnamed)'}</span>
    </button>
  );
}
